import React from 'react';
import clsx from 'clsx';
import {type IconDefinition} from '@fortawesome/fontawesome-common-types';
import {
	composeClasses,
	getSeverityColors,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {
	Title,
	titleClasses,
	type TitleProps,
} from '@netx/core-components/Title';
import {
	ACTION_MENU_HEADER_CLASS_NAME,
	actionMenuHeaderClasses,
	ActionMenuHeaderClasses,
	getActionMenuHeaderUtilityClass,
} from './actionMenuHeaderClasses';

type ActionMenuHeaderOwnerState = WithClasses<
	ActionMenuHeaderClasses,
	{
		avatar?: boolean;
		severity?: ActionMenuHeaderSeverity;
	}
>;

type ActionMenuHeaderSeverity =
	| 'default'
	| 'primary'
	| 'secondary'
	| 'inherit'
	| 'error'
	| 'info'
	| 'success'
	| 'warning';

const useUtilityClasses = (ownerState: ActionMenuHeaderOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		title: ['title'],
	};
	return composeClasses(slots, getActionMenuHeaderUtilityClass, classes);
};

const ActionMenuHeaderRoot = styled('div', {
	name: ACTION_MENU_HEADER_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<ActionMenuHeaderOwnerState>) => {
	const colors = getSeverityColors(theme);
	const {avatar, severity} = ownerState || {};
	return {
		flex: '0 0 auto',
		padding: theme.spacing(1, 2),
		borderRadius: `4px 4px 0 0`,
		...theme.mixins.positionSticky,
		top: 0,
		left: 0,
		zIndex: 2,
		...(severity === 'default' || !severity
			? {
					backgroundColor: theme.palette.background.paper,
					color: theme.palette.text.primary,
			  }
			: {
					// @ts-ignore
					backgroundColor: colors[severity]?.default,
					// @ts-ignore
					color: colors[severity]?.text,
			  }),
		[`& .${titleClasses.icon}`]: {
			...(!avatar
				? {
						height: 24,
						width: 24,
				  }
				: {
						marginRight: 10,
				  }),
			background: 'transparent',
			...(severity && severity !== 'default'
				? {
						// @ts-ignore
						color: colors[severity]?.text,
				  }
				: null),
		},
	};
});

export interface ActionMenuHeaderProps
	extends WithClasses<
		ActionMenuHeaderClasses,
		Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>
	> {
	avatar?: React.ReactElement;
	icon?: IconDefinition;
	secondaryTitle?: React.ReactElement | string | null;
	severity?: ActionMenuHeaderSeverity;
	title?: React.ReactElement | string;
	slotProps?: {
		title?: Partial<TitleProps>;
	};
}

export const ActionMenuHeader = ({
	classes: _classes,
	className,
	avatar,
	icon,
	title,
	secondaryTitle,
	severity,
	slotProps,
	...props
}: ActionMenuHeaderProps) => {
	const ownerState: ActionMenuHeaderOwnerState = {
		classes: _classes,
		className,
		severity,
		avatar: avatar != null,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<ActionMenuHeaderRoot
			className={clsx(classes.root, className)}
			ownerState={ownerState}
			{...props}
		>
			<Title
				className={classes.title}
				avatar={avatar}
				icon={icon}
				primary={title}
				secondary={secondaryTitle}
				{...slotProps?.title}
				PrimaryTitleProps={{
					variant: 'body1',
					...slotProps?.title?.PrimaryTitleProps,
				}}
			/>
		</ActionMenuHeaderRoot>
	);
};

export default ActionMenuHeader;
